import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/ds/shadcn/components/ui/dialog";
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@/ds/shadcn/components/ui/drawer";
import { screenSizeIsConsideredDesktop, useScreenSize } from "@/lib/common/ScreenSizeContext";
import { PortalContainer } from "@/lib/common/usePortalContainer";
import type { DialogDescriptionProps, DialogTitleProps } from "@radix-ui/react-dialog";
import { useCallback, type FC, type PropsWithChildren } from "react";

export interface ModalProps extends PropsWithChildren {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	preventCloseOnInteractOutside?: boolean;
}

export const Modal: FC<ModalProps> = props => {
	const {
		open: isOpen,
		onOpenChange,
		preventCloseOnInteractOutside = false,
		children
	} = props;

	const screenSize = useScreenSize();

	const onInteractOutside = useCallback((e: CustomEvent) => {
		if (!preventCloseOnInteractOutside) {
			return;
		}

		e.preventDefault();
	}, [preventCloseOnInteractOutside]);

	if (screenSizeIsConsideredDesktop(screenSize)) {
		return (
			<Dialog open={isOpen} onOpenChange={onOpenChange} modal={true}>
				<DialogContent onInteractOutside={onInteractOutside}>
					<PortalContainer className="flex flex-col flex-1 min-h-0">
						{children}
					</PortalContainer>
				</DialogContent>
			</Dialog>
		);
	} else {
		return (
			<Drawer open={isOpen} onOpenChange={onOpenChange} modal={true}>
				<DrawerContent onInteractOutside={onInteractOutside}>
					<PortalContainer className="flex flex-col flex-1 min-h-0">
						{children}
					</PortalContainer>
				</DrawerContent>
			</Drawer>
		);
	}
};

export const ModalHeader: FC<PropsWithChildren> = props => {
	const {
		children
	} = props;

	const screenSize = useScreenSize();

	if (screenSizeIsConsideredDesktop(screenSize)) {
		return (
			<DialogHeader className="mb-3">
				{children}
			</DialogHeader>
		);
	} else {
		return (
			<DrawerHeader>
				{children}
			</DrawerHeader>
		);
	}
};

export const ModalTitle: FC<DialogTitleProps> = props => {
	const screenSize = useScreenSize();

	if (screenSizeIsConsideredDesktop(screenSize)) {
		return (
			<DialogTitle {...props} />
		);
	} else {
		return (
			<DrawerTitle {...props} />
		);
	}
};

export const ModalDescription: FC<DialogDescriptionProps> = props => {
	const screenSize = useScreenSize();

	if (screenSizeIsConsideredDesktop(screenSize)) {
		return (
			<DialogDescription {...props} />
		);
	} else {
		return (
			<DrawerDescription {...props} />
		);
	}
};

export const ModalBody: FC<PropsWithChildren> = props => {
	const {
		children
	} = props;

	return (
		<div className="overflow-y-auto flex-1 min-h-0 p-4 @lg:p-0">
			{children}
		</div>
	);
};

export const ModalFooter: FC<PropsWithChildren> = props => {
	const {
		children
	} = props;

	const screenSize = useScreenSize();

	if (screenSizeIsConsideredDesktop(screenSize)) {
		return (
			<DialogFooter className="pt-6">
				{children}
			</DialogFooter>
		);
	} else {
		return (
			<DrawerFooter>
				{children}
			</DrawerFooter>
		);
	}
};
